import Marquee from "react-fast-marquee";
import { ArrowRight } from "lucide-react";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import Seo from "@/components/Seo";
import { STORY_CHAPTERS, BUSINESS } from "@/data/content";

export default function OurStory() {
  return (
    <div data-testid="our-story-page">
      <Seo
        title="Our Story"
        path="/our-story"
        description="The story behind Barrhaven Beauty Lounge — founded by Sheeney to bring premium, personalised skincare and beauty treatments close to home in Nepean, Ottawa."
      />
      <PageHero
        script="our story"
        title="Where beauty meets calm."
        subtitle="Barrhaven Beauty Lounge was founded with a simple goal: to create a calm, elegant space where beauty and self-care come together."
      />

      {/* Feature image */}
      <section className="mx-auto max-w-[1400px] px-5 md:px-10 pt-14 md:pt-20">
        <Reveal>
          <div className="relative h-[46vh] min-h-[320px] overflow-hidden rounded-2xl md:h-[64vh]">
            <img
              src="/story/story1.jpg"
              alt="Barrhaven Beauty Lounge interior"
              className="h-full w-full object-cover"
            />
          </div>
        </Reveal>
      </section>

      {/* Photo gallery */}
      <section className="mx-auto max-w-[1400px] px-5 md:px-10 pt-6 md:pt-10">
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
          {[
            { src: "/story/story2.jpg", alt: "Barrhaven Beauty Lounge treatment area" },
            { src: "/story/story3.jpg", alt: "Inside TradeSecrets Barrhaven" },
          ].map((g, i) => (
            <Reveal key={g.src} delay={i * 0.08}>
              <div className="relative aspect-[4/3] overflow-hidden rounded-2xl bg-[#f5f5f4]">
                <img
                  src={g.src}
                  alt={g.alt}
                  className="h-full w-full object-cover transition-transform duration-700 hover:scale-105"
                />
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Chapters */}
      <section className="mx-auto max-w-[1400px] px-5 md:px-10 py-20 md:py-28">
        <div className="space-y-16 md:space-y-24">
          {STORY_CHAPTERS.map((c) => (
            <Reveal key={c.num}>
              <div className="grid gap-6 border-t border-black/12 pt-10 md:grid-cols-12 md:gap-10">
                <div className="md:col-span-3">
                  <span className="font-serif-display text-6xl md:text-7xl font-light text-[#050505]">
                    {c.num}
                  </span>
                </div>
                <div className="md:col-span-9">
                  <h2 className="font-serif-display text-3xl md:text-4xl tracking-tight">
                    {c.title}
                  </h2>
                  <p className="mt-5 max-w-2xl text-base md:text-lg leading-relaxed text-[#3d3d3d]">
                    {c.body}
                  </p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Marquee */}
      <section className="border-y border-black/10 bg-[#050505] py-6 text-white grain relative overflow-hidden">
        <div className="relative">
          <Marquee speed={40} gradient={false} autoFill>
            <span className="mx-8 font-serif-display text-3xl md:text-4xl italic">
              refreshed · glowing · confident
            </span>
            <span className="mx-8 font-serif-display text-3xl md:text-4xl italic text-white/40">•</span>
          </Marquee>
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-[1400px] px-5 md:px-10 py-24 text-center">
        <Reveal>
          <h2 className="mx-auto max-w-2xl font-serif-display text-4xl md:text-6xl font-light tracking-tight">
            Every detail matters — and every client is treated with care.
          </h2>
          <a
            href={BUSINESS.bookingUrl}
            target="_blank"
            rel="noreferrer"
            className="group mt-10 inline-flex items-center gap-2 bg-[#050505] px-10 py-4 text-sm uppercase tracking-[0.14em] text-white transition-colors duration-300 hover:bg-white hover:text-[#050505] border border-[#050505]"
          >
            Book your visit
            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
          </a>
        </Reveal>
      </section>
    </div>
  );
}
